import React, { useEffect, useState } from 'react';
import { isFirebaseConfigured } from '../../services/firebaseConfig.js';
import { useTelegram } from './useTelegram.js';

type TelegramAuthStatus = 'WEB' | 'AUTHENTICATING' | 'AUTHENTICATED' | 'ERROR';
type AuthenticateTelegram = (initData: string) => Promise<void>;

async function defaultAuthenticate(initData: string): Promise<void> {
  const { authenticateWithTelegram } = await import('./telegramAuth.js');
  await authenticateWithTelegram(initData);
}

interface TelegramAuthBoundaryProps {
  readonly children: React.ReactNode;
  readonly authenticate?: AuthenticateTelegram;
  readonly firebaseConfigured?: boolean;
}

export const TelegramAuthBoundary: React.FC<TelegramAuthBoundaryProps> = ({
  children,
  authenticate = defaultAuthenticate,
  firebaseConfigured = isFirebaseConfigured(),
}) => {
  const { adapter, environment } = useTelegram();
  const [status, setStatus] = useState<TelegramAuthStatus>(
    environment.isTelegram ? 'AUTHENTICATING' : 'WEB'
  );
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!environment.isTelegram) return;

    let active = true;
    const authenticateSession = async (): Promise<void> => {
      setStatus('AUTHENTICATING');
      const initData = adapter.getUnverifiedInitData();
      if (!firebaseConfigured || !initData) {
        setStatus('ERROR');
        return;
      }

      try {
        await authenticate(initData);
        if (active) setStatus('AUTHENTICATED');
      } catch {
        if (active) setStatus('ERROR');
      }
    };
    void authenticateSession();

    return () => {
      active = false;
    };
  }, [adapter, attempt, authenticate, environment.isTelegram, firebaseConfigured]);

  if (!environment.isTelegram) return <>{children}</>;
  if (status === 'WEB' || status === 'AUTHENTICATED') return <>{children}</>;

  if (status === 'AUTHENTICATING') {
    return (
      <main className="telegram-auth-screen" aria-busy="true">
        <div className="telegram-auth-card">
          <span className="telegram-auth-spinner" aria-hidden="true" />
          <h1>Signing you in</h1>
          <p>Securely verifying your Telegram session…</p>
        </div>
      </main>
    );
  }

  return (
    <main className="telegram-auth-screen">
      <div className="telegram-auth-card" role="alert">
        <h1>Unable to sign in</h1>
        <p>Reopen PAY888 from Telegram and try again.</p>
        <button
          type="button"
          className="telegram-auth-retry"
          onClick={() => setAttempt((n) => n + 1)}
        >
          Try again
        </button>
      </div>
    </main>
  );
};
